import useSWR from "swr";
import { useAgentsByIds } from "~/hooks/useAgentList";
import { useFetcher } from "~/hooks/useFetcher";
import type { Agent } from "~/services/agents";
import {
  evalCasePath,
  evalCaseHistoryPath,
  evalCasesPath,
  evalEvaluatorsPath,
  evalRunPath,
  evalRunResultPath,
  evalRunResultsPath,
  evalRunsPath,
  type EvalCase,
  type EvalCaseHistory,
  type EvalCaseResult,
  type EvalPage,
  type EvalRun,
  type PartialEvalCase,
  type PartialEvalCaseResult,
  type EvalRunListItem,
  type PartialEvalRun,
  type SelectableEvaluator,
} from "~/services/evals";
import { apiClient, isApiError } from "~/utils/apiClient";
import { type ResultsByRun, parseApiTimestamp } from "./selectors/core";

/**
 * Cases and runs both arrive snake_cased. Nothing here camelizes: the eval
 * schemas nest free-form JSONB (`inputs`, `expectations`, `evaluator_scores`,
 * `judge_config`) whose keys are data, and camelizing would rewrite them.
 */

export interface PagedEvalItems<T> {
  items: T[];
  /** What the server says matches, which exceeds `items` once truncated. */
  totalCount: number;
  /** The fetch stopped at its own bound with pages still unread. */
  truncated: boolean;
}

/**
 * Every item a list endpoint holds, by following `metadata.has_more`. One page is
 * not the answer for any of these lists: the figures are computed over whatever
 * arrived, so a boundary does not shorten a table, it changes the numbers.
 *
 * `pathAt` takes the offset rather than the URL because each eval path builder
 * writes its own `limit` and repeatable `tags` params cannot be appended to a
 * finished query string — which is also why this does not reuse `fetchAllV1Items`.
 * `maxItems` bounds the walk; reaching it sets `truncated`, which the caller has
 * to surface, since a figure quietly computed over a slice is worse than none.
 */
export async function fetchAllEvalPages<T>(
  pathAt: (offset: number) => string,
  maxItems: number
): Promise<PagedEvalItems<T>> {
  const items: T[] = [];
  for (;;) {
    const page = await apiClient<EvalPage<T>>(pathAt(items.length));
    items.push(...page.items);
    // An empty page with `has_more` still set would loop forever, so a page that
    // adds nothing ends the walk whatever the server claims.
    if (!page.metadata.has_more || page.items.length === 0) {
      return { items, totalCount: page.metadata.total_count, truncated: false };
    }
    if (items.length >= maxItems) {
      return { items, totalCount: page.metadata.total_count, truncated: true };
    }
  }
}

/**
 * Walk bounds, each a whole number of pages at that endpoint's cap so hitting one
 * costs no wasted request: cases and runs page at 200, results at 500.
 */
const MAX_CASES = 2000;
const MAX_RUNS = 2000;
const MAX_RESULTS_PER_RUN = 5000;

export const useEvalCases = (tags: string[] = []) =>
  useSWR<PagedEvalItems<PartialEvalCase>>(evalCasesPath({ tags }), () =>
    fetchAllEvalPages<PartialEvalCase>((offset) => evalCasesPath({ tags, offset }), MAX_CASES)
  );

export const useEvalCase = (caseId: string | null) =>
  useFetcher<EvalCase>(caseId ? evalCasePath(caseId) : null);

/** The evaluators the case form offers, from the backend catalog. */
export const useEvalEvaluators = () => useFetcher<SelectableEvaluator[]>(evalEvaluatorsPath());

/** Runs newest-first, as the API returns them. */
export const useEvalRuns = (tags: string[] = []) =>
  useSWR<PagedEvalItems<EvalRunListItem>>(evalRunsPath({ tags }), () =>
    fetchAllEvalPages<EvalRunListItem>(
      // The list default is a 50-run page; ask for the endpoint's cap so the
      // usual history arrives in one request rather than four.
      (offset) => evalRunsPath({ tags, limit: 200, offset }),
      MAX_RUNS
    )
  );

export const useEvalRun = (runId: string | null) =>
  useFetcher<EvalRun>(runId ? evalRunPath(runId) : null);

export const useEvalRunResults = (runId: string | null) =>
  useSWR<PagedEvalItems<PartialEvalCaseResult>>(runId ? evalRunResultsPath(runId) : null, () =>
    fetchAllEvalPages<PartialEvalCaseResult>(
      (offset) => evalRunResultsPath(runId as string, { offset }),
      MAX_RESULTS_PER_RUN
    )
  );

/**
 * Each case's own most recent runs, in one request. A client-side fan-out over run
 * columns caps how deep a case's history can go — tag-scoped runs bought the median
 * case 8 readings out of 12 requests — so the depth comes from the server or not at all.
 *
 * The run selectors go with it rather than being applied to the response: the
 * window has to be taken after filtering, or narrowing to one agent returns the
 * newest runs minus the other agents'.
 */
export const useEvalCaseHistory = ({
  tags,
  agentIds,
  status,
  runsPerCase,
}: {
  tags: string[];
  agentIds: string[];
  /** Omitted for "any status"; the page's own all-statuses sentinel is not a value. */
  status?: string;
  runsPerCase: number;
}) => useFetcher<EvalCaseHistory>(evalCaseHistoryPath({ tags, agentIds, status, runsPerCase }));

export const useEvalCaseResult = (runId: string | null, resultId: string | null) =>
  useFetcher<EvalCaseResult>(runId && resultId ? evalRunResultPath(runId, resultId) : null);

/**
 * How to write the agents a set of runs name, resolved in one batch for the whole
 * page so every view names the same agent the same way. An `agent_id` alone
 * separates nothing: agents share an orchestrator model.
 *
 * An id the batch does not return is DELETED — nothing else is hidden from an
 * `admin:agents` reader — which is ordinary for a run that outlived its agent, so
 * the short id stands in.
 */
export const useRunAgentLabels = (agentIds: (string | null)[]) => {
  const { data: agents } = useAgentsByIds(agentIds.filter((id): id is string => id != null));
  const byId = new Map((agents ?? []).map((agent) => [agent.id, agent]));

  return {
    /** The agent's display name, or its short id once the agent is gone. */
    agentLabel: (agentId: string | null): string | null =>
      agentId == null ? null : (byId.get(agentId)?.display_name ?? `${agentId.slice(0, 8)}…`),
    /** Whether the name is real, so a caller can mark a stand-in as one. */
    isNamed: (agentId: string | null): boolean => agentId != null && byId.has(agentId),
    /** The agent itself, for what a name cannot carry — its avatar. Undefined once it is gone. */
    agentRecord: (agentId: string | null): Agent | undefined =>
      agentId == null ? undefined : byId.get(agentId),
  };
};

export type RunAgentLabels = ReturnType<typeof useRunAgentLabels>;

/** Chronological order — oldest first, the order the matrix and trend expect. */
export function sortRunsChronologically(runs: PartialEvalRun[]): PartialEvalRun[] {
  return [...runs].sort(
    (a, b) => parseApiTimestamp(a.started_at) - parseApiTimestamp(b.started_at)
  );
}

export interface RunResultsBundle {
  resultsByRun: ResultsByRun;
  /** Runs the caller cannot read; the table marks their outcome columns. */
  unavailableRunIds: string[];
  /** Runs whose results failed to load. Retryable, unlike the unreadable ones. */
  failedRunIds: string[];
  /** Runs holding more results than the walk will read, so their figures are a sample. */
  truncatedRunIds: string[];
}

interface RunResultsOutcome {
  kind: "loaded" | "unavailable" | "failed";
  /** Empty unless `loaded`, which is what the table renders for an unread run. */
  results: PartialEvalCaseResult[];
  truncated: boolean;
}

/**
 * One run's results, walked to exhaustion.
 *
 * A 404 is the access decision, not a fault — the results route 404s where the
 * caller cannot read a dataset or agent the run touched. Any other failure is
 * confined to its own run rather than rejecting the caller's whole batch, but it
 * is reported rather than absorbed.
 */
async function fetchRunResults(runId: string): Promise<RunResultsOutcome> {
  try {
    const page = await fetchAllEvalPages<PartialEvalCaseResult>(
      (offset) => evalRunResultsPath(runId, { offset }),
      MAX_RESULTS_PER_RUN
    );
    return { kind: "loaded", results: page.items, truncated: page.truncated };
  } catch (error) {
    if (isApiError(error) && error.status === 404) {
      return { kind: "unavailable", results: [], truncated: false };
    }
    console.error(`Failed to load results for eval run ${runId}`, error);
    return { kind: "failed", results: [], truncated: false };
  }
}

/**
 * Results for several runs at once, keyed by run id. The API has no cross-run
 * results endpoint, so this is a client-side join: one request per run.
 *
 * Every requested run gets a key. An absent key means the run was never asked
 * about, which `buildRunRows` reads as unmeasured.
 */
export async function collectResultsForRuns(runIds: string[]): Promise<RunResultsBundle> {
  const outcomes = await Promise.all(runIds.map(fetchRunResults));
  const idsWhere = (kind: RunResultsOutcome["kind"]) =>
    runIds.filter((_, index) => outcomes[index].kind === kind);

  return {
    resultsByRun: Object.fromEntries(
      runIds.map((runId, index) => [runId, outcomes[index].results])
    ),
    unavailableRunIds: idsWhere("unavailable"),
    failedRunIds: idsWhere("failed"),
    truncatedRunIds: runIds.filter((_, index) => outcomes[index].truncated),
  };
}

/** The whole bundle under one SWR key, so the table renders once. */
export const useResultsForRuns = (runIds: string[]) =>
  useSWR<RunResultsBundle>(runIds.length > 0 ? ["eval-results-by-run", ...runIds] : null, () =>
    collectResultsForRuns(runIds)
  );
